import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
    Plus, Send, Trash2, ClipboardList, Loader2, Pencil, Save, BarChart3, TrendingUp, Package as PackageIcon
} from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BAR_OPTIONS = [
    { value: 'lobby', label: 'Lobby Bar' },
    { value: 'servis', label: 'Servis Bar' },
    { value: 'vitamin', label: 'Vitamin Bar' },
    { value: 'havuz', label: 'Havuz Bar' },
    { value: 'plaj', label: 'Plaj Bar' },
    { value: 'amfi', label: 'Amfi Bar' },
    { value: 'teras', label: 'Teras Bar' },
    { value: 'alacarte', label: 'Alacarte Bar' },
    { value: 'minibar', label: 'Minibar' },
];

const UNITS = [
    { value: 'adet', label: 'Adet' },
    { value: 'cl', label: 'CL' },
    { value: 'litre', label: 'Litre' },
    { value: 'koli', label: 'Koli' },
    { value: 'box', label: 'Box' },
    { value: 'paket', label: 'Paket' },
    { value: 'kg', label: 'KG' },
    { value: 'sarf', label: 'Sarf Malzemesi' },
    { value: 'diğer', label: 'Diğer' },
];

export function Talepler() {
    const { user } = useAuth();
    const role = (user?.role || '').toLowerCase();
    // v6: Üst yönetici de Talepler modülünde düzenle/sil yapabilir
    const canEdit = ['admin', 'management', 'manager', 'ust_yonetici', 'bar_sef'].includes(role);
    const [selectedBar, setSelectedBar] = useState('lobby');
    // Filter for viewing sent requests — 'all' = tüm barlar
    const [filterBar, setFilterBar] = useState('all');
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [items, setItems] = useState([{ product_name: '', unit: 'adet', quantity: '' }]);
    const [suggestions, setSuggestions] = useState([]);
    const [activeIndex, setActiveIndex] = useState(null);
    const [sending, setSending] = useState(false);
    const [sentRequests, setSentRequests] = useState([]);
    const [loadingRequests, setLoadingRequests] = useState(false);

    const barLabel = (v) => BAR_OPTIONS.find(b => b.value === v)?.label || v;
    // Inline edit state: req.id → { items: [...] } (draft)
    const [editingId, setEditingId] = useState(null);
    const [editDraft, setEditDraft] = useState(null);

    const startEdit = (req) => {
        setEditingId(req.id);
        setEditDraft({ items: (req.items || []).map(it => ({ ...it, quantity: String(it.quantity ?? '') })) });
    };
    const cancelEdit = () => { setEditingId(null); setEditDraft(null); };
    const updateDraftItem = (idx, field, val) => {
        setEditDraft(d => ({ ...d, items: d.items.map((it, i) => i === idx ? { ...it, [field]: val } : it) }));
    };
    const addDraftItem = () => setEditDraft(d => ({ ...d, items: [...d.items, { product_name: '', unit: 'adet', quantity: '' }] }));
    const removeDraftItem = (idx) => setEditDraft(d => ({ ...d, items: d.items.filter((_, i) => i !== idx) }));
    const saveEdit = async (id) => {
        // Validate: at least 1 item with product_name + quantity>0
        const cleanItems = (editDraft?.items || [])
            .map(it => ({ product_name: (it.product_name || '').trim(), unit: it.unit || 'adet', quantity: Number(String(it.quantity).replace(',', '.')) || 0 }))
            .filter(it => it.product_name && it.quantity > 0);
        if (cleanItems.length === 0) {
            toast.error('En az 1 geçerli ürün olmalı (ürün adı + miktar > 0)');
            return;
        }
        try {
            await api.put(`/bar-requests/${id}`, { items: cleanItems });
            toast.success('Talep güncellendi');
            setSentRequests(prev => prev.map(r => r.id === id ? { ...r, items: cleanItems } : r));
            cancelEdit();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Güncelleme başarısız');
        }
    };

    const updateRequestStatus = async (id, newStatus) => {
        try {
            await api.put(`/bar-requests/${id}`, { status: newStatus });
            toast.success(newStatus === 'approved' ? 'Talep onaylandı' : 'Talep reddedildi');
            setSentRequests(prev => prev.map(r => r.id === id ? { ...r, status: newStatus } : r));
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Durum güncellenemedi');
        }
    };
    const fetchSentRequests = async () => {
        try {
            setLoadingRequests(true);
            const token = localStorage.getItem('token');
            const barParam = filterBar === 'all' ? '' : `bar_id=${filterBar}&`;
            const res = await fetch(`${API_URL}/api/bar-requests?${barParam}date=${selectedDate}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) setSentRequests(await res.json());
        } catch (e) { console.error(e); }
        finally { setLoadingRequests(false); }
    };

    const deleteSentRequest = async (id) => {
        if (!window.confirm('Bu talebi silmek istediginize emin misiniz?')) return;
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/bar-requests/${id}`, {
                method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                toast.success('Talep silindi');
                setSentRequests(prev => prev.filter(r => r.id !== id));
            } else {
                const err = await res.json().catch(() => ({}));
                toast.error(err.detail || 'Silme basarisiz');
            }
        } catch { toast.error('Silme hatasi'); }
    };

    useEffect(() => { fetchSentRequests(); }, [filterBar, selectedDate]);

    // ===================== ANALITİK =====================
    const [analyticsDays, setAnalyticsDays] = useState(7);
    const [analytics, setAnalytics] = useState(null);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);

    const fetchAnalytics = async () => {
        try {
            setAnalyticsLoading(true);
            const res = await api.get('/bar-requests/analytics', { params: { days: analyticsDays } });
            setAnalytics(res.data);
        } catch (e) {
            console.error(e);
        } finally {
            setAnalyticsLoading(false);
        }
    };
    useEffect(() => { fetchAnalytics(); }, [analyticsDays]);
    // Refresh analytics when a new request is created/deleted
    useEffect(() => { fetchAnalytics(); }, [sentRequests.length]);

    const handleSearch = async (value, index) => {
        setActiveIndex(index);
        const newItems = [...items];
        newItems[index].product_name = value;
        setItems(newItems);
        if (value.length >= 1) {
            try {
                const res = await api.get('/products/autocomplete', { params: { q: value } });
                setSuggestions(res.data || []);
            } catch { setSuggestions([]); }
        } else { setSuggestions([]); }
    };

    const selectProduct = (index, product) => {
        const newItems = [...items];
        newItems[index].product_name = product.name;
        newItems[index].unit = product.unit || 'adet';
        setItems(newItems);
        setSuggestions([]);
        setActiveIndex(null);
    };

    const updateItem = (index, field, value) => {
        const newItems = [...items];
        newItems[index][field] = value;
        setItems(newItems);
    };

    const addRow = () => {
        setItems([...items, { product_name: '', unit: 'adet', quantity: '' }]);
    };

    const removeRow = (index) => {
        if (items.length <= 1) {
            setItems([{ product_name: '', unit: 'adet', quantity: '' }]);
            return;
        }
        setItems(items.filter((_, i) => i !== index));
    };

    const sendRequest = async () => {
        const validItems = items.filter(i => i.product_name && parseFloat(i.quantity) > 0);
        if (validItems.length === 0) {
            toast.error('En az bir urun ve miktar giriniz');
            return;
        }
        try {
            setSending(true);
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/bar-requests`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    bar_id: selectedBar,
                    date: selectedDate,
                    items: validItems.map(i => ({ ...i, quantity: parseFloat(i.quantity) })),
                })
            });
            if (res.ok) {
                const body = await res.json().catch(() => ({}));
                if (body?.merged) {
                    toast.success('Aynı bar için mevcut talebe eklendi — Sedna\'ya iletilecek');
                } else {
                    toast.success('Talep oluşturuldu — Sedna\'ya iletiliyor');
                }
                setItems([{ product_name: '', unit: 'adet', quantity: '' }]);
                fetchSentRequests();
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Talep gonderilemedi');
            }
        } catch { toast.error('Talep gonderme hatasi'); }
        finally { setSending(false); }
    };

    // Analitik özet (gösterilen listeye göre)
    const stats = React.useMemo(() => {
        const total = sentRequests.length;
        let pending = 0, approved = 0, rejected = 0, sednaSource = 0, sednaPushed = 0, sednaWaiting = 0, sednaError = 0;
        const perBar = {};
        for (const r of sentRequests) {
            if (r.source === 'sedna') sednaSource++;
            if (r.status === 'pending') pending++;
            else if (r.status === 'approved') approved++;
            else if (r.status === 'rejected') rejected++;
            if (r.sedna_pushed_at) sednaPushed++;
            else if (r.status === 'approved' && !r.sedna_push_error) sednaWaiting++;
            if (r.sedna_push_error) sednaError++;
            const b = r.bar_id || '?';
            if (!perBar[b]) perBar[b] = { requests: 0, items: 0, qty: 0 };
            perBar[b].requests++;
            perBar[b].items += (r.items || []).length;
            perBar[b].qty += (r.items || []).reduce((s, it) => s + (Number(it.quantity) || 0), 0);
        }
        return { total, pending, approved, rejected, sednaSource, sednaPushed, sednaWaiting, sednaError, perBar };
    }, [sentRequests]);

    return (
        <div className="space-y-5" data-testid="talepler-page">
            {/* Premium Header */}
            <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-5 text-white shadow-xl relative overflow-hidden">
                <div className="absolute inset-0 opacity-[0.12] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 15% 20%, #f97316 0%, transparent 45%), radial-gradient(circle at 85% 80%, #10b981 0%, transparent 45%)' }} />
                <div className="relative flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-lg">
                            <ClipboardList className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold tracking-tight">Bar Talepleri</h1>
                            <p className="text-[11px] text-slate-400 mt-0.5">Bar Stok&apos;tan gelen talepler · Otomatik Sedna F&amp;B Talep Fişi oluşturma</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <Select value={filterBar} onValueChange={setFilterBar}>
                            <SelectTrigger className="w-44 h-9 bg-slate-800/60 border-slate-700 text-white text-xs" data-testid="talep-filter-bar">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">🏨 Tüm Barlar</SelectItem>
                                {BAR_OPTIONS.map(b => (
                                    <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)}
                            className="w-40 h-9 bg-slate-800/60 border-slate-700 text-white text-xs" data-testid="talep-date" />
                    </div>
                </div>
            </div>

            {/* KPI Özet Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                {[
                    { lbl: 'Toplam Talep', v: stats.total, col: 'from-slate-700 to-slate-800' },
                    { lbl: 'Beklemede', v: stats.pending, col: 'from-amber-500 to-orange-600' },
                    { lbl: 'Onaylı', v: stats.approved, col: 'from-emerald-500 to-teal-600' },
                    { lbl: 'Reddedildi', v: stats.rejected, col: 'from-red-500 to-red-600' },
                    { lbl: 'Sedna Kaynaklı', v: stats.sednaSource, col: 'from-indigo-500 to-indigo-700' },
                    { lbl: '✓ Sedna Yansıdı', v: stats.sednaPushed, col: 'from-emerald-600 to-emerald-700' },
                    { lbl: '⏳ Sedna Bekliyor', v: stats.sednaWaiting, col: 'from-amber-600 to-amber-700' },
                ].map(k => (
                    <div key={k.lbl} className={`bg-gradient-to-br ${k.col} text-white rounded-xl p-3 shadow-md`}>
                        <div className="text-[10px] uppercase tracking-wider text-white/80">{k.lbl}</div>
                        <div className="text-xl font-bold mt-0.5">{k.v}</div>
                    </div>
                ))}
            </div>

            {/* Analytics Panel — Haftalık/Aylık Trend */}
            <Card className="border-slate-200">
                <CardHeader className="pb-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <CardTitle className="text-base flex items-center gap-2">
                            <BarChart3 className="w-5 h-5 text-indigo-500" />
                            Talep Analitiği
                            <span className="text-[10px] text-slate-400 font-normal">
                                {analytics?.range?.start} — {analytics?.range?.end}
                            </span>
                        </CardTitle>
                        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
                            {[7, 30, 90].map(d => (
                                <button key={d}
                                    onClick={() => setAnalyticsDays(d)}
                                    data-testid={`analytics-range-${d}`}
                                    className={`px-3 py-1 rounded-md text-xs font-medium transition ${analyticsDays === d ? 'bg-white shadow-sm text-indigo-700' : 'text-slate-500 hover:text-slate-700'}`}>
                                    {d === 7 ? 'Haftalık' : d === 30 ? 'Aylık' : '90 Gün'}
                                </button>
                            ))}
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    {analyticsLoading && !analytics ? (
                        <div className="py-8 text-center text-slate-400 text-sm">
                            <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Yükleniyor...
                        </div>
                    ) : !analytics || analytics.total_requests === 0 ? (
                        <div className="py-8 text-center text-slate-400 text-sm">
                            Bu aralıkta henüz talep yok
                        </div>
                    ) : (
                        <>
                            {/* Analytics KPI Row */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 border border-indigo-200 rounded-lg p-3">
                                    <div className="text-[10px] uppercase tracking-wider text-indigo-600 font-medium">Toplam Talep</div>
                                    <div className="text-2xl font-bold text-indigo-900 mt-0.5">{analytics.total_requests}</div>
                                </div>
                                <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-lg p-3">
                                    <div className="text-[10px] uppercase tracking-wider text-orange-600 font-medium">Toplam Kalem</div>
                                    <div className="text-2xl font-bold text-orange-900 mt-0.5">{analytics.total_items}</div>
                                </div>
                                <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 rounded-lg p-3">
                                    <div className="text-[10px] uppercase tracking-wider text-emerald-600 font-medium">Toplam Miktar</div>
                                    <div className="text-2xl font-bold text-emerald-900 mt-0.5">{analytics.total_qty}</div>
                                </div>
                                <div className="bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 rounded-lg p-3">
                                    <div className="text-[10px] uppercase tracking-wider text-slate-600 font-medium">Aktif Bar</div>
                                    <div className="text-2xl font-bold text-slate-900 mt-0.5">{analytics.by_bar.length}</div>
                                </div>
                            </div>

                            {/* Line Chart — Günlük Trend */}
                            <div>
                                <div className="text-xs font-medium text-slate-600 mb-2 flex items-center gap-1.5">
                                    <TrendingUp className="w-3.5 h-3.5" /> Günlük Talep Trendi
                                </div>
                                <div className="h-56 bg-slate-50 rounded-lg p-2">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={analytics.series} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                            <XAxis dataKey="date" tick={{ fontSize: 10 }}
                                                tickFormatter={(d) => d?.slice(5)} />
                                            <YAxis tick={{ fontSize: 10 }} />
                                            <Tooltip contentStyle={{ fontSize: 11 }} />
                                            <Line type="monotone" dataKey="qty" stroke="#f97316" strokeWidth={2} name="Miktar" dot={{ r: 3 }} />
                                            <Line type="monotone" dataKey="items" stroke="#6366f1" strokeWidth={2} name="Kalem" dot={{ r: 3 }} />
                                            <Line type="monotone" dataKey="requests" stroke="#10b981" strokeWidth={2} name="Talep" dot={{ r: 3 }} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                {/* Bar Chart — Bar Bazında Miktar */}
                                <div>
                                    <div className="text-xs font-medium text-slate-600 mb-2 flex items-center gap-1.5">
                                        <BarChart3 className="w-3.5 h-3.5" /> Bar Bazında Miktar Dağılımı
                                    </div>
                                    <div className="h-64 bg-slate-50 rounded-lg p-2">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={analytics.by_bar} margin={{ top: 5, right: 10, left: 0, bottom: 30 }}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                                <XAxis dataKey="bar_label" tick={{ fontSize: 9 }} angle={-25} textAnchor="end" interval={0} />
                                                <YAxis tick={{ fontSize: 10 }} />
                                                <Tooltip contentStyle={{ fontSize: 11 }} />
                                                <Bar dataKey="qty" fill="#f97316" name="Miktar" radius={[4, 4, 0, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>

                                {/* Top Products */}
                                <div>
                                    <div className="text-xs font-medium text-slate-600 mb-2 flex items-center gap-1.5">
                                        <PackageIcon className="w-3.5 h-3.5" /> En Çok İstenen Ürünler (Top 10)
                                    </div>
                                    <div className="bg-slate-50 rounded-lg overflow-hidden">
                                        <table className="w-full text-xs">
                                            <thead className="bg-slate-100 text-slate-600">
                                                <tr>
                                                    <th className="text-left px-2 py-1.5 font-medium">#</th>
                                                    <th className="text-left px-2 py-1.5 font-medium">Ürün</th>
                                                    <th className="text-right px-2 py-1.5 font-medium">Miktar</th>
                                                    <th className="text-right px-2 py-1.5 font-medium">Talep</th>
                                                    <th className="text-right px-2 py-1.5 font-medium">Bar</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-200">
                                                {analytics.top_products.map((p, i) => (
                                                    <tr key={p.product_name} className="hover:bg-white" data-testid={`top-product-${i}`}>
                                                        <td className="px-2 py-1.5 text-slate-400">{i + 1}</td>
                                                        <td className="px-2 py-1.5 font-medium text-slate-800 truncate max-w-[160px]" title={p.product_name}>{p.product_name}</td>
                                                        <td className="px-2 py-1.5 text-right font-bold text-orange-700">{p.total_qty}<span className="text-[9px] text-slate-400 ml-0.5">{p.unit}</span></td>
                                                        <td className="px-2 py-1.5 text-right text-slate-600">{p.requests}</td>
                                                        <td className="px-2 py-1.5 text-right text-slate-500">{p.bar_count}</td>
                                                    </tr>
                                                ))}
                                                {analytics.top_products.length === 0 && (
                                                    <tr><td colSpan="5" className="text-center py-4 text-slate-400">Veri yok</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Bar Bazında Talep Dağılımı */}
            {Object.keys(stats.perBar).length > 0 && (
                <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm">📊 Bar Bazında Talep Dağılımı</CardTitle></CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                            {Object.entries(stats.perBar)
                                .sort((a, b) => b[1].requests - a[1].requests)
                                .map(([barId, s]) => (
                                    <div key={barId} className="bg-slate-50 border rounded-lg p-2.5">
                                        <div className="text-xs font-semibold text-slate-800 truncate">{barLabel(barId)}</div>
                                        <div className="text-[10px] text-slate-500 mt-0.5">{s.requests} talep · {s.items} kalem</div>
                                        <div className="text-sm font-bold text-orange-700 mt-1">{s.qty} adet</div>
                                    </div>
                                ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Yeni Talep Oluştur (bar seçimi burada, form açık) */}
            <Card>
                <CardHeader className="pb-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <CardTitle className="text-base flex items-center gap-2">
                            <Plus className="w-5 h-5 text-orange-500" />
                            Yeni Talep Oluştur
                        </CardTitle>
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-500">Bar:</span>
                            <Select value={selectedBar} onValueChange={setSelectedBar}>
                                <SelectTrigger className="w-40 h-8 text-xs" data-testid="talep-bar-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {BAR_OPTIONS.map(b => (
                                        <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Button variant="outline" size="sm" onClick={addRow}>
                                <Plus className="w-4 h-4 mr-1" /> Satır Ekle
                            </Button>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-3">
                    {/* Header */}
                    <div className="hidden md:flex items-center gap-2 text-xs text-slate-500 font-medium px-1">
                        <div className="flex-1">Ürün Adı</div>
                        <div className="w-28">Birim</div>
                        <div className="w-24 text-center">Miktar</div>
                        <div className="w-8"></div>
                    </div>

                    {items.map((item, idx) => (
                        <div key={idx} className="flex flex-col md:flex-row items-start md:items-center gap-2 p-2 md:p-0 bg-slate-50 md:bg-transparent rounded-lg md:rounded-none" onClick={(e) => e.stopPropagation()}>
                            {/* Product name with autocomplete */}
                            <div className="flex-1 relative w-full">
                                <Input
                                    value={item.product_name}
                                    onChange={(e) => handleSearch(e.target.value, idx)}
                                    onFocus={() => setActiveIndex(idx)}
                                    placeholder="Ürün adı yazın..."
                                    className="h-10"
                                    data-testid={`talep-product-${idx}`}
                                />
                                {activeIndex === idx && suggestions.length > 0 && (
                                    <div className="absolute z-50 left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-auto">
                                        {suggestions.map((p) => (
                                            <div key={p.id}
                                                className="px-3 py-2.5 hover:bg-slate-100 cursor-pointer flex justify-between items-center"
                                                onMouseDown={(e) => { e.preventDefault(); selectProduct(idx, p); }}>
                                                <span className="text-sm font-medium">{p.name}</span>
                                                <Badge variant="outline" className="text-xs uppercase">{p.unit}</Badge>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Unit selector */}
                            <Select value={item.unit} onValueChange={(v) => updateItem(idx, 'unit', v)}>
                                <SelectTrigger className="w-full md:w-28 h-10" data-testid={`talep-unit-${idx}`}>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {UNITS.map(u => (
                                        <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>

                            {/* Quantity */}
                            <Input
                                type="number"
                                value={item.quantity}
                                onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                                placeholder="Miktar"
                                className="w-full md:w-24 h-10 text-center"
                                data-testid={`talep-qty-${idx}`}
                            />

                            {/* Remove */}
                            <Button variant="ghost" size="icon" className="h-10 w-10 text-red-400 hover:text-red-600 flex-shrink-0"
                                onClick={() => removeRow(idx)} data-testid={`talep-remove-${idx}`}>
                                <Trash2 className="w-4 h-4" />
                            </Button>
                        </div>
                    ))}
                </CardContent>
            </Card>

            {/* Send Button */}
            <div className="flex justify-end">
                <Button
                    onClick={sendRequest}
                    disabled={sending}
                    size="lg"
                    className="bg-orange-600 hover:bg-orange-700 px-8"
                    data-testid="send-request-btn"
                >
                    {sending ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Send className="w-5 h-5 mr-2" />}
                    {sending ? 'Gonderiliyor...' : 'Talep Gonder'}
                </Button>
            </div>

            {/* Sent Requests for selected bar & date */}
            {sentRequests.length > 0 ? (
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                            <ClipboardList className="w-5 h-5 text-blue-500" />
                            Gönderilmiş Talepler ({sentRequests.length})
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {(() => {
                            const grouped = {};
                            for (const r of sentRequests) {
                                const b = r.bar_id || '?';
                                if (!grouped[b]) grouped[b] = [];
                                grouped[b].push(r);
                            }
                            return Object.keys(grouped).sort().map(barId => {
                                const reqs = grouped[barId];
                                const gTotalQty = reqs.reduce((s, r) => s + (r.items || []).reduce((s2, it) => s2 + (Number(it.quantity) || 0), 0), 0);
                                const gTotalItems = reqs.reduce((s, r) => s + (r.items || []).length, 0);
                                return (
                                    <div key={barId} className="rounded-xl border border-slate-200 bg-slate-50/40 overflow-hidden">
                                        <div className="bg-gradient-to-r from-slate-800 to-slate-700 text-white px-4 py-2.5 flex items-center justify-between flex-wrap gap-2">
                                            <div className="flex items-center gap-2">
                                                <span className="text-base font-bold">🏨 {barLabel(barId)}</span>
                                                <Badge className="bg-white/20 text-white border-white/30 text-[10px]">{reqs.length} talep</Badge>
                                            </div>
                                            <div className="text-xs text-slate-200">
                                                <b>{gTotalItems}</b> kalem · <b className="text-orange-300">{gTotalQty}</b> adet
                                            </div>
                                        </div>
                                        <div className="p-2 space-y-2">
                                        {reqs.map((req) => {
                            const isSedna = req.source === 'sedna';
                            const pushedToSedna = !!req.sedna_pushed_at;
                            const pushError = req.sedna_push_error;
                            const isPending = req.status === 'pending';
                            const isApproved = req.status === 'approved';
                            const isRejected = req.status === 'rejected';
                            const totalQty = (req.items || []).reduce((s, it) => s + (Number(it.quantity) || 0), 0);
                            // Kartın soluna renkli şerit (durum bazlı)
                            const stripeColor = pushedToSedna ? 'border-emerald-500' : pushError ? 'border-red-500' : isApproved ? 'border-amber-400' : isRejected ? 'border-slate-400' : isPending ? 'border-orange-400' : 'border-indigo-400';
                            return (
                            <div key={req.id} className={`bg-white rounded-lg border-l-4 ${stripeColor} border shadow-sm hover:shadow-md transition-shadow`} data-testid={`sent-req-${req.id}`}>
                                {/* Kart Başlık — Bar adı + Rozetler + Zaman */}
                                <div className="flex items-start justify-between gap-2 p-3 pb-2 border-b border-slate-100">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                                            <span className="text-xs text-slate-500">{req.date}</span>
                                            {req.created_by_name && (
                                                <>
                                                    <span className="text-xs text-slate-400">·</span>
                                                    <span className="text-xs text-slate-500">{req.created_by_name}</span>
                                                </>
                                            )}
                                            <span className="text-xs text-slate-400 ml-auto">
                                                {req.created_at ? new Date(req.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : ''}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            {isSedna ? (
                                                <Badge className="text-[10px] bg-indigo-100 text-indigo-700 border-indigo-300" data-testid={`req-sedna-badge-${req.id}`}>
                                                    📡 Sedna Kaynaklı
                                                </Badge>
                                            ) : isRejected ? (
                                                <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-300"
                                                    data-testid={`req-status-${req.id}`}>
                                                    ✗ Reddedildi
                                                </Badge>
                                            ) : req.status === 'delete_pending' ? (
                                                <Badge className="text-[10px] bg-red-100 text-red-700 border-red-300"
                                                    data-testid={`req-status-${req.id}`}>
                                                    🗑 Sedna&apos;dan Silinmesi Bekleniyor
                                                </Badge>
                                            ) : null}
                                            {!isSedna && isApproved && pushedToSedna && (
                                                <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-300"
                                                    title={`Sedna'ya yansıdı: ${new Date(req.sedna_pushed_at).toLocaleString('tr-TR')}`}
                                                    data-testid={`req-sedna-pushed-${req.id}`}>
                                                    ✓ Sedna F&B Talep Fişine İletildi{req.sedna_slip_no ? ` · ${req.sedna_slip_no}` : ''}
                                                </Badge>
                                            )}
                                            {!isSedna && isApproved && !pushedToSedna && !pushError && req.sedna_action === 'update' && (
                                                <Badge className="text-[10px] bg-blue-100 text-blue-800 border-blue-300"
                                                    data-testid={`req-sedna-update-pending-${req.id}`}>
                                                    ✎ Sedna Güncellenmesi Bekleniyor
                                                </Badge>
                                            )}
                                            {!isSedna && isApproved && !pushedToSedna && !pushError && req.sedna_action !== 'update' && (
                                                <Badge className="text-[10px] bg-amber-100 text-amber-800 border-amber-300">
                                                    ⏳ Sedna&apos;ya İletilmeyi Bekliyor
                                                </Badge>
                                            )}
                                            {!isSedna && pushError && (
                                                <Badge className="text-[10px] bg-red-100 text-red-700 border-red-300"
                                                    title={`Sedna hatası: ${pushError}`}
                                                    data-testid={`req-sedna-error-${req.id}`}>
                                                    ⚠ Sedna Hatası
                                                </Badge>
                                            )}
                                            <span className="text-[10px] text-slate-500 ml-1">
                                                {(req.items || []).length} kalem · {totalQty} adet
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Kart Gövdesi — Ürün Tablosu VEYA Düzenleme Formu */}
                                <div className="p-3 pt-2">
                                    {editingId === req.id ? (
                                        <div className="space-y-1.5" data-testid={`edit-form-${req.id}`}>
                                            {(editDraft?.items || []).map((it, i) => (
                                                <div key={i} className="flex items-center gap-2 bg-slate-50 p-1.5 rounded border">
                                                    <Input value={it.product_name} onChange={(e) => updateDraftItem(i, 'product_name', e.target.value)}
                                                        placeholder="Ürün adı" className="h-8 text-sm flex-1" data-testid={`edit-product-${req.id}-${i}`} />
                                                    <Input type="text" inputMode="decimal" value={it.quantity}
                                                        onChange={(e) => updateDraftItem(i, 'quantity', e.target.value)}
                                                        onFocus={(e) => e.target.select()}
                                                        placeholder="0" className="h-8 text-sm w-20 text-center" data-testid={`edit-qty-${req.id}-${i}`} />
                                                    <Select value={it.unit || 'adet'} onValueChange={(v) => updateDraftItem(i, 'unit', v)}>
                                                        <SelectTrigger className="h-8 w-24 text-sm" data-testid={`edit-unit-${req.id}-${i}`}><SelectValue /></SelectTrigger>
                                                        <SelectContent>
                                                            {['adet', 'litre', 'kg', 'gr', 'cl', 'ml', 'kutu', 'paket', 'şişe'].map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                                                        </SelectContent>
                                                    </Select>
                                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-600"
                                                        onClick={() => removeDraftItem(i)} data-testid={`edit-remove-${req.id}-${i}`}>
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </Button>
                                                </div>
                                            ))}
                                            <div className="flex justify-between items-center pt-1">
                                                <Button variant="ghost" size="sm" className="h-7 text-xs text-emerald-600" onClick={addDraftItem} data-testid={`edit-add-${req.id}`}>
                                                    <Plus className="w-3 h-3 mr-1" /> Ürün Ekle
                                                </Button>
                                                <div className="flex gap-1">
                                                    <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                                                        onClick={() => saveEdit(req.id)} data-testid={`edit-save-${req.id}`}>
                                                        <Save className="w-3 h-3 mr-1" /> Kaydet
                                                    </Button>
                                                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={cancelEdit} data-testid={`edit-cancel-${req.id}`}>
                                                        İptal
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <table className="w-full text-xs">
                                            <thead className="text-slate-500">
                                                <tr className="border-b border-slate-100">
                                                    <th className="text-left py-1 pr-2 font-medium">Ürün</th>
                                                    <th className="text-center py-1 px-2 font-medium w-20">Miktar</th>
                                                    <th className="text-center py-1 px-2 font-medium w-20">Birim</th>
                                                    <th className="text-right py-1 pl-2 font-medium w-24">Sedna Kodu</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(req.items || []).map((it, i) => (
                                                    <tr key={i} className="border-b border-slate-50 last:border-0">
                                                        <td className="py-1.5 pr-2 text-slate-800 font-medium">{it.product_name}</td>
                                                        <td className="py-1.5 px-2 text-center font-semibold text-orange-700">{it.quantity}</td>
                                                        <td className="py-1.5 px-2 text-center text-slate-500 uppercase text-[10px]">{it.unit}</td>
                                                        <td className="py-1.5 pl-2 text-right">
                                                            {it.sedna_code ? (
                                                                <span className="font-mono text-[10px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded">{it.sedna_code}</span>
                                                            ) : (
                                                                <span className="text-[10px] text-red-500" title="Sedna kodu yok — ürünü Ürünler sayfasında bul & kod ata">⚠ kod yok</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>

                                {/* Kart Alt — Aksiyonlar (v6: onay yok, direkt Sedna) */}
                                {editingId !== req.id && (
                                    <div className="flex items-center gap-1 justify-end px-3 py-2 bg-slate-50/50 border-t border-slate-100 rounded-b-lg">
                                        {!isSedna && canEdit && (
                                            <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px] text-slate-600 hover:text-slate-800 hover:bg-slate-100"
                                                onClick={() => startEdit(req)} data-testid={`edit-req-${req.id}`}
                                                title={pushedToSedna ? "Düzenlendiğinde Sedna\'da UPDATE olarak yansır" : "Düzenle"}>
                                                <Pencil className="w-3.5 h-3.5 mr-1" /> Düzenle
                                            </Button>
                                        )}
                                        {!isSedna && canEdit && (
                                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600"
                                                onClick={() => deleteSentRequest(req.id)} data-testid={`delete-sent-${req.id}`}
                                                title={pushedToSedna ? "Sil — Sedna\'dan da kaldırılır" : "Talebi sil"}>
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </Button>
                                        )}
                                        {isSedna && (
                                            <span className="text-[10px] text-slate-400 italic">Sedna kaynaklı kayıt — CRM&apos;den düzenlenemez</span>
                                        )}
                                    </div>
                                )}
                            </div>
                            );
                        })}
                                        </div>
                                    </div>
                                );
                            });
                        })()}
                    </CardContent>
                </Card>
            ) : (
                !loadingRequests && (
                    <Card>
                        <CardContent className="py-8 text-center text-slate-400 text-sm">
                            {filterBar === 'all' ? 'Bu tarihte hiçbir barda talep yok' : `${barLabel(filterBar)} için bu tarihte talep yok`}
                        </CardContent>
                    </Card>
                )
            )}
        </div>
    );
}

export default Talepler;
